/**
 * EditorOptionsWindow
 *
 * A ToolWindow for configuring editor-specific settings like
 * panning strength and zoom speed.
 */

import React from 'react';
import { ToolWindow } from '../../components/ToolWindow';
import { EditorState } from '../../../systems/interfaces/IEditorStateManager';
import { EditorSystem } from '../../../systems/editor/EditorSystem';

interface EditorOptionsWindowProps {
  isOpen: boolean;
  onClose: () => void;
  state: EditorState;
  editorSystem: EditorSystem | null;
}

interface MapResizeSectionProps {
  editorSystem: EditorSystem | null;
  isOpen: boolean;
  sectionStyle: React.CSSProperties;
  labelStyle: React.CSSProperties;
  valueStyle: React.CSSProperties;
}

const MapResizeSection: React.FC<MapResizeSectionProps> = ({
  editorSystem,
  isOpen,
  sectionStyle,
  labelStyle,
  valueStyle
}) => {
  const [widthInput, setWidthInput] = React.useState('');
  const [heightInput, setHeightInput] = React.useState('');

  const dims = React.useMemo(() => {
    return editorSystem?.getMapDimensions() ?? null;
  }, [editorSystem, isOpen]);

  React.useEffect(() => {
    if (dims) {
      setWidthInput(String(dims.width));
      setHeightInput(String(dims.height));
    }
  }, [isOpen, dims?.width, dims?.height]);

  const handleResize = () => {
    const w = parseInt(widthInput, 10);
    const h = parseInt(heightInput, 10);
    if (!isNaN(w) && !isNaN(h)) {
      editorSystem?.resizeMap(w, h);
    }
  };

  return (
    <div style={sectionStyle}>
      <div style={labelStyle}>
        <span>Map Size (Tiles)</span>
        <span style={valueStyle}>{dims ? `${dims.width} × ${dims.height}` : 'N/A'}</span>
      </div>
      <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
        <div style={{ flex: 1 }}>
          <span style={{ fontSize: '10px', color: '#95a5a6' }}>Width</span>
          <input
            type="number"
            min="10"
            max="500"
            value={widthInput}
            onChange={(e) => setWidthInput(e.target.value)}
            style={{
              width: '100%',
              padding: '6px',
              backgroundColor: 'rgba(0, 0, 0, 0.4)',
              border: '1px solid rgba(255, 255, 255, 0.15)',
              borderRadius: '4px',
              color: '#fff',
              fontSize: '12px',
              fontFamily: 'monospace',
              boxSizing: 'border-box'
            }}
          />
        </div>
        <div style={{ flex: 1 }}>
          <span style={{ fontSize: '10px', color: '#95a5a6' }}>Height</span>
          <input
            type="number"
            min="10"
            max="500"
            value={heightInput}
            onChange={(e) => setHeightInput(e.target.value)}
            style={{
              width: '100%',
              padding: '6px',
              backgroundColor: 'rgba(0, 0, 0, 0.4)',
              border: '1px solid rgba(255, 255, 255, 0.15)',
              borderRadius: '4px',
              color: '#fff',
              fontSize: '12px',
              fontFamily: 'monospace',
              boxSizing: 'border-box'
            }}
          />
        </div>
      </div>
      <button
        onClick={handleResize}
        style={{
          marginTop: '8px',
          width: '100%',
          padding: '8px',
          backgroundColor: '#3498db',
          color: '#fff',
          border: 'none',
          borderRadius: '4px',
          cursor: 'pointer',
          fontSize: '11px',
          fontWeight: 'bold',
          transition: 'background-color 0.15s'
        }}
      >
        Apply Resize
      </button>
      <div style={{ fontSize: '9px', color: '#7f8c8d', marginTop: '2px' }}>
        Warning: Shrinking the map prunes out-of-bounds tiles & entities.
      </div>
    </div>
  );
};

export const EditorOptionsWindow: React.FC<EditorOptionsWindowProps> = ({
  isOpen,
  onClose,
  state,
  editorSystem
}) => {
  const sectionStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    marginBottom: '20px',
    padding: '10px',
    backgroundColor: 'rgba(0, 0, 0, 0.2)',
    borderRadius: '6px',
    border: '1px solid rgba(255, 255, 255, 0.05)'
  };

  const labelStyle: React.CSSProperties = {
    fontSize: '11px',
    color: '#bdc3c7',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    fontWeight: 'bold',
    display: 'flex',
    justifyContent: 'space-between'
  };

  const sliderStyle: React.CSSProperties = {
    width: '100%',
    cursor: 'pointer',
    accentColor: '#3498db'
  };

  const valueStyle: React.CSSProperties = {
    color: '#3498db',
    fontFamily: 'monospace'
  };

  if (!isOpen) return null;

  return (
    <ToolWindow
      id="editor-options"
      title="Editor Options"
      icon="⚙️"
      isOpen={isOpen}
      onClose={onClose}
      defaultPosition={{ x: window.innerWidth / 2 - 150, y: 150 }}
      defaultSize={{ width: 300, height: 480 }}
      resizable={true}
      collapsible={true}
    >
      <div style={{ padding: '10px', color: 'white', fontFamily: 'Inter, sans-serif' }}>
        <p style={{ fontSize: '12px', color: '#95a5a6', marginBottom: '20px' }}>
          Configure your editor experience and navigation settings.
        </p>

        {/* Pan Strength */}
        <div style={sectionStyle}>
          <div style={labelStyle}>
            <span>Pan Strength</span>
            <span style={valueStyle}>{state.panAmount}px</span>
          </div>
          <input
            type="range"
            min="100"
            max="2000"
            step="50"
            value={state.panAmount}
            onChange={(e) => editorSystem?.getStateManager().setPanAmount(parseInt(e.target.value))}
            style={sliderStyle}
          />
          <div style={{ fontSize: '10px', color: '#7f8c8d' }}>
            Amount the camera moves per click on the D-pad.
          </div>
        </div>

        {/* Zoom Speed */}
        <div style={sectionStyle}>
          <div style={labelStyle}>
            <span>Zoom Speed</span>
            <span style={valueStyle}>{state.zoomSpeed.toFixed(2)}x</span>
          </div>
          <input
            type="range"
            min="0.01"
            max="0.5"
            step="0.01"
            value={state.zoomSpeed}
            onChange={(e) => editorSystem?.getStateManager().setZoomSpeed(parseFloat(e.target.value))}
            style={sliderStyle}
          />
          <div style={{ fontSize: '10px', color: '#7f8c8d' }}>
            Increments for mouse wheel and zoom buttons.
          </div>
        </div>

        {/* Map Resize */}
        <MapResizeSection editorSystem={editorSystem} isOpen={isOpen} sectionStyle={sectionStyle} labelStyle={labelStyle} valueStyle={valueStyle} />

        <div style={{ marginTop: '20px', borderTop: '1px solid rgba(255, 255, 255, 0.1)', paddingTop: '15px' }}>
          <button
            onClick={() => editorSystem?.getStateManager().reset()}
            style={{
              width: '100%',
              padding: '10px',
              backgroundColor: 'rgba(231, 76, 60, 0.1)',
              color: '#e74c3c',
              border: '1px solid #e74c3c',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: 'bold',
              transition: 'all 0.2s'
            }}
          >
            Reset to Defaults
          </button>
        </div>
      </div>
    </ToolWindow>
  );
};
